export interface ThemeColors {
  primary: string;
  primaryDim: string;
  primaryText: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  surfaceHover: string;
  surfaceActive: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  borderLight: string;
  // New contrast and border properties
  onPrimary: string;
  onSecondary?: string;
  onSurface: string;
  onBackground: string;
  onAccent: string;
  borderSubtle: string;
  borderStrong: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  // Presence
  online: string;
  busy: string;
  dnd: string;
  offline: string;
  // Priority
  high: string;
  medium: string;
  low: string;
  // Sidebar
  sidebarBg: string;
  sidebarBorder: string;
  sidebarHover: string;
  // Input
  inputBg: string;
  inputBorder: string;
  inputFocus: string;
  // Map
  mapTile: string;
  // Gradients
  avatarFrom: string;
  avatarTo: string;
  // Shadows
  glow: string;
}

export interface ThemeEffects {
  borderRadius: string;
  cardRadius: string;
  animation: string;
  glowShadow: string;
  gradient: string;
  backdropBlur?: string;
  // NEW EFFECTS
  hoverScale?: string;
  pulseIntensity?: 'subtle' | 'medium' | 'strong';
  transitionDuration?: string;
  transitionTiming?: string;
  cardGlow?: string;
  textGlow?: string;
  borderGlow?: string;
  particleEffect?: boolean;
  shimmer?: boolean;
  floatingAnimation?: boolean;
  glassIntensity?: 'light' | 'medium' | 'heavy';
  accentGradient?: string;
  hoverGlow?: string;
}

export interface Theme {
  id: string;
  name: string;
  description: string;
  emoji: string;
  isDark: boolean;
  colors: ThemeColors;
  effects: ThemeEffects;
  preview: {
    sidebar: string;
    content: string;
    card: string;
    accent: string;
  };
}

export const themes: Record<string, Theme> = {
  // ===== EXISTING THEMES =====
  dark: {
    id: 'dark',
    name: 'Midnight',
    description: 'Default dark theme with blue accents',
    emoji: '🌙',
    isDark: true,
    colors: {
      primary: '#3b82f6',
      primaryDim: 'rgba(59, 130, 246, 0.2)',
      primaryText: '#60a5fa',
      secondary: '#6366f1',
      accent: '#8b5cf6',
      background: '#0f172a',
      surface: '#1e293b',
      surfaceHover: '#334155',
      surfaceActive: '#3b4d6b',
      text: '#f1f5f9',
      textSecondary: '#94a3b8',
      textMuted: '#64748b',
      border: '#334155',
      borderLight: '#475569',
      onPrimary: '#ffffff',
      onSurface: '#f1f5f9',
      onBackground: '#f1f5f9',
      onAccent: '#ffffff',
      borderSubtle: '#1e293b',
      borderStrong: '#64748b',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      online: '#10b981',
      busy: '#ef4444',
      dnd: '#f59e0b',
      offline: '#64748b',
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#10b981',
      sidebarBg: '#0f172a',
      sidebarBorder: '#1e293b',
      sidebarHover: '#1e293b',
      inputBg: '#1e293b',
      inputBorder: '#334155',
      inputFocus: '#3b82f6',
      mapTile: 'dark',
      avatarFrom: '#3b82f6',
      avatarTo: '#8b5cf6',
      glow: 'none',
    },
    effects: {
      borderRadius: '0.75rem',
      cardRadius: '1rem',
      animation: 'all 0.2s ease',
      glowShadow: 'none',
      gradient: 'linear-gradient(135deg, #3b82f6, #6366f1)',
      hoverScale: '1.02',
      transitionDuration: '0.2s',
      transitionTiming: 'ease',
    },
    preview: {
      sidebar: '#0f172a',
      content: '#1e293b',
      card: '#334155',
      accent: '#3b82f6',
    },
  },

  light: {
    id: 'light',
    name: 'Default Light',
    description: 'Premium creamy off-white with deep obsidian text',
    emoji: '☀️',
    isDark: false,
    colors: {
      primary: '#0f172a',
      primaryDim: 'rgba(15, 23, 42, 0.1)',
      primaryText: '#0f172a',
      secondary: '#334155',
      accent: '#2563eb',
      background: '#f8fafc',
      surface: '#ffffff',
      surfaceHover: '#f1f5f9',
      surfaceActive: '#e2e8f0',
      text: '#1e293b',
      textSecondary: '#475569',
      textMuted: '#64748b',
      border: '#cbd5e1',
      borderLight: '#e2e8f0',
      onPrimary: '#ffffff',
      onSurface: '#0f172a',
      onBackground: '#0f172a',
      onAccent: '#ffffff',
      borderSubtle: '#f1f5f9',
      borderStrong: '#94a3b8',
      success: '#059669',
      warning: '#d97706',
      error: '#dc2626',
      info: '#2563eb',
      online: '#059669',
      busy: '#dc2626',
      dnd: '#7c3aed',
      offline: '#94a3b8',
      high: '#dc2626',
      medium: '#d97706',
      low: '#059669',
      sidebarBg: '#ffffff',
      sidebarBorder: '#e2e8f0',
      sidebarHover: '#f8fafc',
      inputBg: '#ffffff',
      inputBorder: '#cbd5e1',
      inputFocus: '#2563eb',
      mapTile: 'light',
      avatarFrom: '#0f172a',
      avatarTo: '#334155',
      glow: '0 10px 40px rgba(0, 0, 0, 0.03)',
    },
    effects: {
      borderRadius: '0.75rem',
      cardRadius: '1.25rem',
      animation: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      glowShadow: '0 4px 20px -1px rgba(0, 0, 0, 0.05)',
      gradient: 'linear-gradient(135deg, #1e293b, #475569)',
      hoverScale: '1.02',
      transitionDuration: '0.3s',
    },
    preview: {
      sidebar: '#ffffff',
      content: '#f8fafc',
      card: '#ffffff',
      accent: '#3b82f6',
    },
  },

  glass: {
    id: 'glass',
    name: 'iOS Glassmorphism',
    description: 'High-fidelity frosted glass with crystalline depth',
    emoji: '✨',
    isDark: true,
    colors: {
      primary: '#ffffff',
      primaryDim: 'rgba(255, 255, 255, 0.2)',
      primaryText: '#ffffff',
      secondary: '#94a3b8',
      accent: '#ffffff',
      background: '#040712',
      surface: 'rgba(255, 255, 255, 0.08)',
      surfaceHover: 'rgba(255, 255, 255, 0.12)',
      surfaceActive: 'rgba(255, 255, 255, 0.15)',
      text: '#ffffff',
      textSecondary: '#e2e8f0',
      textMuted: '#94a3b8',
      border: 'rgba(255, 255, 255, 0.18)',
      borderLight: 'rgba(255, 255, 255, 0.08)',
      onPrimary: '#000000',
      onSurface: '#ffffff',
      onBackground: '#ffffff',
      onAccent: '#000000',
      borderSubtle: 'rgba(255, 255, 255, 0.05)',
      borderStrong: 'rgba(255, 255, 255, 0.3)',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      online: '#10b981',
      busy: '#ef4444',
      dnd: '#f59e0b',
      offline: '#64748b',
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#10b981',
      sidebarBg: 'rgba(255, 255, 255, 0.03)',
      sidebarBorder: 'rgba(255, 255, 255, 0.1)',
      sidebarHover: 'rgba(255, 255, 255, 0.08)',
      inputBg: 'rgba(255, 255, 255, 0.05)',
      inputBorder: 'rgba(255, 255, 255, 0.1)',
      inputFocus: '#ffffff',
      mapTile: 'dark',
      avatarFrom: '#ffffff',
      avatarTo: '#94a3b8',
      glow: '0 0 40px rgba(255, 255, 255, 0.15)',
    },
    effects: {
      borderRadius: '1.5rem',
      cardRadius: '2rem',
      animation: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
      glowShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
      gradient: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.02))',
      backdropBlur: 'blur(32px)',
      hoverScale: '1.03',
      glassIntensity: 'heavy',
      shimmer: true,
      hoverGlow: '0 0 60px rgba(255, 255, 255, 0.1)',
    },
    preview: {
      sidebar: 'rgba(15, 23, 42, 0.8)',
      content: 'rgba(15, 23, 42, 0.6)',
      card: 'rgba(255, 255, 255, 0.05)',
      accent: '#ffffff',
    },
  },

  oled: {
    id: 'oled',
    name: 'OLED Black',
    description: 'Pure obsidian for absolute minimalism and power saving',
    emoji: '⬛',
    isDark: true,
    colors: {
      primary: '#ffffff',
      primaryDim: 'rgba(255, 255, 255, 0.1)',
      primaryText: '#ffffff',
      secondary: '#71717a',
      accent: '#ffffff',
      background: '#000000',
      surface: '#000000',
      surfaceHover: '#09090b',
      surfaceActive: '#121214',
      text: '#ffffff',
      textSecondary: '#a1a1aa',
      textMuted: '#52525b',
      border: '#18181b',
      borderLight: '#09090b',
      onPrimary: '#000000',
      onSurface: '#ffffff',
      onBackground: '#ffffff',
      onAccent: '#000000',
      borderSubtle: '#09090b',
      borderStrong: '#27272a',
      success: '#ffffff',
      warning: '#ffffff',
      error: '#ff3333',
      info: '#ffffff',
      online: '#ffffff',
      busy: '#ff3333',
      dnd: '#ffffff',
      offline: '#27272a',
      high: '#ff3333',
      medium: '#a1a1aa',
      low: '#52525b',
      sidebarBg: '#000000',
      sidebarBorder: '#18181b',
      sidebarHover: '#09090b',
      inputBg: '#000000',
      inputBorder: '#18181b',
      inputFocus: '#ffffff',
      mapTile: 'dark',
      avatarFrom: '#ffffff',
      avatarTo: '#71717a',
      glow: 'none',
    },
    effects: {
      borderRadius: '0.5rem',
      cardRadius: '0.75rem',
      animation: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      glowShadow: '0 0 0 1px #27272a',
      gradient: 'linear-gradient(135deg, #ffffff, #000000)',
      hoverScale: '1.02',
    },
    preview: {
      sidebar: '#000000',
      content: '#000000',
      card: '#0a0a0a',
      accent: '#ffffff',
    },
  },


  blackMarble: {
    id: 'black-marble',
    name: 'Black Marble',
    description: 'Luxurious black with subtle white veining',
    emoji: '⚫',
    isDark: true,
    colors: {
      primary: '#ffffff',
      primaryDim: 'rgba(255, 255, 255, 0.1)',
      primaryText: '#ffffff',
      secondary: '#cccccc',
      accent: '#e0e0e0',
      background: '#0a0a0a',
      surface: '#1a1a1a',
      surfaceHover: '#252525',
      surfaceActive: '#303030',
      text: '#ffffff',
      textSecondary: '#cccccc',
      textMuted: '#999999',
      border: '#333333',
      borderLight: '#2a2a2a',
      onPrimary: '#000000',
      onSurface: '#ffffff',
      onBackground: '#ffffff',
      onAccent: '#000000',
      borderSubtle: '#1f1f1f',
      borderStrong: '#4d4d4d',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#ffffff',
      online: '#10b981',
      busy: '#ef4444',
      dnd: '#f59e0b',
      offline: '#666666',
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#10b981',
      sidebarBg: '#0a0a0a',
      sidebarBorder: '#1a1a1a',
      sidebarHover: '#1a1a1a',
      inputBg: '#1a1a1a',
      inputBorder: '#333333',
      inputFocus: '#ffffff',
      mapTile: 'dark',
      avatarFrom: '#ffffff',
      avatarTo: '#cccccc',
      glow: '0 0 20px rgba(255, 255, 255, 0.05)',
    },
    effects: {
      borderRadius: '0.75rem',
      cardRadius: '1rem',
      animation: 'all 0.2s ease',
      glowShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
      gradient: 'linear-gradient(135deg, #ffffff, #cccccc)',
      hoverScale: '1.02',
    },
    preview: {
      sidebar: '#0a0a0a',
      content: '#1a1a1a',
      card: '#252525',
      accent: '#ffffff',
    },
  },

  whiteMarble: {
    id: 'white-marble',
    name: 'White Marble',
    description: 'Elegant white with soft gray veining',
    emoji: '⚪',
    isDark: false,
    colors: {
      primary: '#1a1a1a',
      primaryDim: 'rgba(26, 26, 26, 0.1)',
      primaryText: '#1a1a1a',
      secondary: '#4a4a4a',
      accent: '#6b6b6b',
      background: '#f5f5f5',
      surface: '#ffffff',
      surfaceHover: '#f0f0f0',
      surfaceActive: '#e8e8e8',
      text: '#1a1a1a',
      textSecondary: '#4a4a4a',
      textMuted: '#6b6b6b',
      border: '#d4d4d4',
      borderLight: '#e8e8e8',
      onPrimary: '#ffffff',
      onSurface: '#1a1a1a',
      onBackground: '#1a1a1a',
      onAccent: '#ffffff',
      borderSubtle: '#ebebeb',
      borderStrong: '#a3a3a3',
      success: '#059669',
      warning: '#d97706',
      error: '#dc2626',
      info: '#1a1a1a',
      online: '#059669',
      busy: '#dc2626',
      dnd: '#7c3aed',
      offline: '#9ca3af',
      high: '#dc2626',
      medium: '#d97706',
      low: '#059669',
      sidebarBg: '#f5f5f5',
      sidebarBorder: '#e0e0e0',
      sidebarHover: '#f0f0f0',
      inputBg: '#fafafa',
      inputBorder: '#e0e0e0',
      inputFocus: '#1a1a1a',
      mapTile: 'light',
      avatarFrom: '#1a1a1a',
      avatarTo: '#4a4a4a',
      glow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    effects: {
      borderRadius: '0.75rem',
      cardRadius: '1rem',
      animation: 'all 0.2s ease',
      glowShadow: '0 2px 10px rgba(0,0,0,0.03)',
      gradient: 'linear-gradient(135deg, #1a1a1a, #4a4a4a)',
      hoverScale: '1.01',
    },
    preview: {
      sidebar: '#f5f5f5',
      content: '#ffffff',
      card: '#ffffff',
      accent: '#1a1a1a',
    },
  },

  // ===== RESTORED NEON THEME =====
  neon: {
    id: 'neon',
    name: 'Neon Glow',
    description: 'Vibrant cyberpunk red and electric blue on obsidian',
    emoji: '🚨',
    isDark: true,
    colors: {
      primary: '#ff3366',
      primaryDim: 'rgba(255, 51, 102, 0.2)',
      primaryText: '#ff3366',
      secondary: '#00ccff',
      accent: '#ffffff',
      background: '#050505',
      surface: '#000000',
      surfaceHover: '#0a0a0a',
      surfaceActive: '#121212',
      text: '#ffffff',
      textSecondary: '#bbbbbb',
      textMuted: '#666666',
      border: '#ff3366',
      borderLight: 'rgba(255, 51, 102, 0.3)',
      onPrimary: '#000000',
      onSurface: '#ffffff',
      onBackground: '#ffffff',
      onAccent: '#000000',
      borderSubtle: 'rgba(0, 204, 255, 0.1)',
      borderStrong: '#00ccff',
      success: '#00ffcc',
      warning: '#ffff33',
      error: '#ff3366',
      info: '#00ccff',
      online: '#00ffcc',
      busy: '#ff3366',
      dnd: '#ff3366',
      offline: '#333333',
      high: '#ff3366',
      medium: '#00ccff',
      low: '#ffffff',
      sidebarBg: '#050505',
      sidebarBorder: '#111111',
      sidebarHover: '#0a0a0a',
      inputBg: '#0a0a0a',
      inputBorder: '#111111',
      inputFocus: '#ff3366',
      mapTile: 'dark',
      avatarFrom: '#ff3366',
      avatarTo: '#00ccff',
      glow: '0 0 20px rgba(255, 51, 102, 0.3)',
    },
    effects: {
      borderRadius: '0.375rem',
      cardRadius: '0.5rem',
      animation: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
      glowShadow: '0 0 30px rgba(255, 51, 102, 0.4)',
      gradient: 'linear-gradient(135deg, #ff3366, #00ccff)',
      hoverScale: '1.03',
      pulseIntensity: 'subtle',
      cardGlow: '0 0 15px rgba(255, 51, 102, 0.15)',
      textGlow: '0 0 8px rgba(255, 255, 255, 0.25)',
      borderGlow: '0 0 10px rgba(255, 51, 102, 0.2)',
    },
    preview: {
      sidebar: '#000000',
      content: '#050505',
      card: '#0a0a0a',
      accent: '#ff3366',
    },
  },

  // ===== RESTORED MOON THEME =====
  moon: {
    id: 'moon',
    name: 'Moonlight',
    description: 'Ethereal luminescent black and white with crystalline depth',
    emoji: '🌕',
    isDark: true,
    colors: {
      primary: '#ffffff',
      primaryDim: 'rgba(255, 255, 255, 0.15)',
      primaryText: '#ffffff',
      secondary: '#f1f5f9',
      accent: '#ffffff',
      background: '#000000',
      surface: '#0d0d0d',
      surfaceHover: '#121212',
      surfaceActive: '#1a1a1a',
      text: '#ffffff',
      textSecondary: '#f1f5f9',
      textMuted: '#94a3b8',
      border: 'rgba(255, 255, 255, 0.4)',
      borderLight: 'rgba(255, 255, 255, 0.2)',
      onPrimary: '#000000',
      onSecondary: '#000000',
      onSurface: '#ffffff',
      onBackground: '#ffffff',
      onAccent: '#000000',
      borderSubtle: 'rgba(255, 255, 255, 0.2)',
      borderStrong: 'rgba(255, 255, 255, 0.6)',
      success: '#ffffff',
      warning: '#ffffff',
      error: '#fee2e2',
      info: '#ffffff',
      online: '#ffffff',
      busy: '#fee2e2',
      dnd: '#ffffff',
      offline: '#4b5563',
      high: '#ffffff',
      medium: '#e5e7eb',
      low: '#9ca3af',
      sidebarBg: '#000000',
      sidebarBorder: 'rgba(255, 255, 255, 0.3)',
      sidebarHover: '#0d0d0d',
      inputBg: '#050505',
      inputBorder: 'rgba(255, 255, 255, 0.3)',
      inputFocus: '#ffffff',
      mapTile: 'dark',
      avatarFrom: '#ffffff',
      avatarTo: '#a0a0a0',
      glow: '0 0 50px rgba(255, 255, 255, 0.1)',
    },
    effects: {
      borderRadius: '1.5rem',
      cardRadius: '2rem',
      animation: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
      glowShadow: '0 0 80px rgba(157, 0, 255, 0.2)',
      gradient: 'linear-gradient(135deg, #ffffff, #a0a0a0)',
      accentGradient: 'linear-gradient(135deg, #ffffff, #e2e8f0, #a0a0a0)',
      hoverScale: '1.05',
      hoverGlow: '0 0 60px rgba(157, 0, 255, 0.3)',
      pulseIntensity: 'medium',
      cardGlow: '0 0 50px rgba(157, 0, 255, 0.2)',
      textGlow: '0 0 25px rgba(255, 255, 255, 0.4)',
      borderGlow: '0 0 20px rgba(255, 255, 255, 0.3)',
      floatingAnimation: true,
    },
    preview: {
      sidebar: '#000000',
      content: '#000000',
      card: '#0d0d0d',
      accent: '#ffffff',
    },
  },

  // ===== NEW LUXURY THEMES =====
  platinum: {
    id: 'platinum',
    name: 'Platinum',
    description: 'Metallic silver with executive-grade gradients',
    emoji: '💎',
    isDark: true,
    colors: {
      primary: '#e5e7eb',
      primaryDim: 'rgba(229, 231, 235, 0.2)',
      primaryText: '#f3f4f6',
      secondary: '#9ca3af',
      accent: '#d1d5db',
      background: '#111111',
      surface: '#1f2937',
      surfaceHover: '#374151',
      surfaceActive: '#4b5563',
      text: '#f9fafb',
      textSecondary: '#e5e7eb',
      textMuted: '#9ca3af',
      border: '#374151',
      borderLight: '#4b5563',
      onPrimary: '#000000',
      onSurface: '#f9fafb',
      onBackground: '#f9fafb',
      onAccent: '#000000',
      borderSubtle: '#1f2937',
      borderStrong: '#6b7280',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#9ca3af',
      online: '#10b981',
      busy: '#ef4444',
      dnd: '#f59e0b',
      offline: '#6b7280',
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#10b981',
      sidebarBg: '#111111',
      sidebarBorder: '#1f2937',
      sidebarHover: '#1f2937',
      inputBg: '#1f2937',
      inputBorder: '#374151',
      inputFocus: '#e5e7eb',
      mapTile: 'dark',
      avatarFrom: '#9ca3af',
      avatarTo: '#e5e7eb',
      glow: '0 0 20px rgba(255, 255, 255, 0.1)',
    },
    effects: {
      borderRadius: '0.75rem',
      cardRadius: '1rem',
      animation: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      glowShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
      gradient: 'linear-gradient(135deg, #374151, #9ca3af, #d1d5db)',
      hoverScale: '1.03',
      pulseIntensity: 'subtle',
      transitionDuration: '0.4s',
      transitionTiming: 'cubic-bezier(0.4, 0, 0.2, 1)',
      cardGlow: '0 0 15px rgba(255, 255, 255, 0.05)',
      shimmer: true,
    },
    preview: {
      sidebar: '#111827',
      content: '#1f2937',
      card: '#374151',
      accent: '#e5e7eb',
    },
  },

  cyberpunk: {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    description: 'Neon pinks, purples, and cyans on dark',
    emoji: '🌃',
    isDark: true,
    colors: {
      primary: '#ff00ff',
      primaryDim: 'rgba(255, 0, 255, 0.1)',
      primaryText: '#ffaaff',
      secondary: '#00ffff',
      accent: '#ffff00',
      background: '#0d0d1a',
      surface: '#1a1a2e',
      surfaceHover: '#252540',
      surfaceActive: '#303052',
      text: '#ffffff',
      textSecondary: '#ffb3ff',
      textMuted: '#6666cc',
      border: 'rgba(255, 0, 255, 0.4)',
      borderLight: 'rgba(0, 255, 255, 0.3)',
      onPrimary: '#ffffff',
      onSurface: '#ffffff',
      onBackground: '#ffffff',
      onAccent: '#000000',
      borderSubtle: 'rgba(255, 0, 255, 0.2)',
      borderStrong: 'rgba(0, 255, 255, 0.5)',
      success: '#00ff88',
      warning: '#ffff00',
      error: '#ff3366',
      info: '#00ffff',
      online: '#00ff88',
      busy: '#ff3366',
      dnd: '#ff00ff',
      offline: '#333366',
      high: '#ff3366',
      medium: '#ffff00',
      low: '#00ff88',
      sidebarBg: '#0d0d1a',
      sidebarBorder: 'rgba(255, 0, 255, 0.2)',
      sidebarHover: '#14142b',
      inputBg: '#0d0d1a',
      inputBorder: 'rgba(255, 0, 255, 0.3)',
      inputFocus: '#00ffff',
      mapTile: 'dark',
      avatarFrom: '#ff00ff',
      avatarTo: '#00ffff',
      glow: '0 0 10px #ff00ff, 0 0 20px #00ffff',
    },
    effects: {
      borderRadius: '0.375rem',
      cardRadius: '0.5rem',
      animation: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      glowShadow: '0 0 15px rgba(255, 0, 255, 0.2), 0 0 30px rgba(0, 255, 255, 0.15)',
      gradient: 'linear-gradient(135deg, #ff00ff, #00ffff)',
      hoverScale: '1.02',
      pulseIntensity: 'subtle',
      transitionDuration: '0.3s',
      cardGlow: '0 0 15px rgba(255, 0, 255, 0.15)',
      textGlow: '0 0 5px rgba(255, 255, 255, 0.1)',
      borderGlow: '0 0 5px currentColor',
      shimmer: true,
    },
    preview: {
      sidebar: '#0d0d1a',
      content: '#1a1a2e',
      card: '#252540',
      accent: '#ff00ff',
    },
  },
};

// Update themeOrder to include all your themes
export const themeOrder = [
  'dark',           // Midnight (default)
  'light',          // Default Light
  'glass',          // iOS Glassmorphism
  'oled',           // OLED Black
  'black-marble',   // Black Marble
  'white-marble',   // White Marble
  'neon',           // Neon Glow
  'moon',           // Moonlight
  'platinum',       // Platinum
  'cyberpunk',      // Cyberpunk
] as const;

export type ThemeId = typeof themeOrder[number];